import {
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography
} from "@mui/material"
import { useTheme } from "@mui/material/styles"

import React from "react"
import _sumBy from "lodash/sumBy"
import { RadialChart } from "react-vis"
import styled from "styled-components"

const Wrapper = styled.div`
  > header {
    text-align: center;
  }
`

const TitleWrap = styled.div`
  min-height: 68px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const DonutWrap = styled.div`
  padding: 1rem 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`

const TotalWrap = styled.div`
  position: absolute;
  text-align: center;
  width: 50%;
`

const ValuesTableWrap = styled.div`
  padding: 1rem 0;

  td {
    white-space: nowrap;

    > p {
      display: inline-block;
    }
  }
`

const Swatch = styled.span<{ color: string }>`
  width: 14px;
  height: 14px;
  display: inline-block;
  margin: 0 0.5rem 0 0;
  background: ${p => p.color};
  position: relative;
  bottom: -1px;
`

type TDonutDatum = {
  label: string
  value: number
}

type TTotalFormatter = (value: number) => string

interface IProps {
  /**
   * Custom series colors. This will override colorRotate.
   * @default []
   */
  colors: string[]
  /**
   * Rotate the series palette from the theme
   * @default 0
   */
  colorRotate: 0 | 1 | 2 | 3 | 4 | 5 | 6
  /**
   * The bars data
   * @default []
   */
  data: TDonutDatum[]
  /**
   * The donut radius, in pixels
   * @default 100
   */
  radius: number
  /**
   * Additional styles to be added to the wrapper for layout purposes
   * @default {}
   */
  styles: React.CSSProperties
  /**
   * A subtitle to be displayed above the bars
   * @default ""
   */
  subtitle: string
  /**
   * Show the total value
   */
  showTotal: boolean
  /**
   * Show the percentages in a table underneath the chart
   */
  showPercentages: boolean
  /**
   * Show the absolute values in a table underneath the chart
   */
  showValues: boolean
  /**
   * A title to be displayed above the bars
   * @default ""
   */
  title: string
  /**
   * The label to show at the top of the chart, next to the series total
   * @default "Total"
   */
  totalLabel: string
  /**
   * A function which will format the display of the total value
   * @default v => `${v}`
   */
  totalFormatter: TTotalFormatter
  /**
   * A prefix to be added just before the values in the table rows. e.g. $
   * @default ""
   */
  valuePrefix: string
  /**
   * A suffix to be added just after the values in the table rows. e.g. %
   * @default ""
   */
  valueSuffix: string
}

type TDonutChartDatum = { index: number; value: number; label: string }

const rotateArr = (arr: string[], n: number) => {
  if (n <= 0) {
    return arr
  }
  const newArr = [...arr]
  let i = n
  while (i > 0) {
    const elm = newArr.shift()
    if (typeof elm === "string") {
      newArr.push(elm)
    }
    i--
  }
  return newArr
}

export const Donut = ({
  colors = [],
  colorRotate = 0,
  data = [],
  radius = 100,
  styles = {},
  subtitle = "",
  title = "",
  totalLabel = "Total",
  showTotal = true,
  showPercentages = false,
  showValues = true,
  valuePrefix = "",
  valueSuffix = "",
  totalFormatter = v => `${v}`
}: IProps) => {
  const theme = useTheme()
  const seriesColors: string[] =
    colors && Array.isArray(colors) && colors.length > 3 && theme.series
      ? colors
      : rotateArr(theme.series || [], colorRotate)
  const maxSeriesLength = seriesColors.length

  if (!data || !data.length) {
    return <p>No Data</p>
  }

  if (data.length > maxSeriesLength) {
    return <p>Max series length: {maxSeriesLength}</p>
  }

  const total = _sumBy(data, "value")
  const showTable = showPercentages || showValues
  const enhancedData = data.map((d, index) => ({
    ...d,
    index
  }))

  return (
    <Wrapper style={{ ...styles }}>
      <header>
        {title ? (
          <>
            <TitleWrap>
              <Typography variant="h4" gutterBottom>
                {title}
              </Typography>
            </TitleWrap>
            <Divider style={{ marginBottom: "1rem" }} />
          </>
        ) : (
          ""
        )}
      </header>
      <DonutWrap>
        <RadialChart
          colorType={"literal"}
          colorDomain={[0, 100]}
          colorRange={[0, 10]}
          getColor={(d: TDonutChartDatum) =>
            seriesColors[d.index % maxSeriesLength]
          }
          data={enhancedData}
          getAngle={(d: TDonutChartDatum) => d.value}
          innerRadius={radius - 20}
          radius={radius - 10}
          padAngle={0.04}
          width={radius * 2}
          height={radius * 2}
          data-testid="chart"
        />
        {showTotal ? (
          <TotalWrap>
            <Typography variant="h4">
              {valuePrefix}
              {totalFormatter(total)}
              {valueSuffix}
            </Typography>
            <Typography style={{ opacity: 0.8 }}>{totalLabel}</Typography>
          </TotalWrap>
        ) : (
          ""
        )}
      </DonutWrap>
      {showTable ? (
        <ValuesTableWrap>
          <Table size="small">
            <TableBody>
              {data.map((d, i) => (
                <TableRow key={d.label}>
                  <TableCell align="left">
                    <Swatch color={seriesColors[i]} />
                    <Typography>{d.label}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    {showPercentages ? (
                      <Typography
                        style={{ opacity: 0.5, marginRight: "0.5rem" }}
                      >
                        {((d.value * 100) / total).toFixed(1)}%
                      </Typography>
                    ) : (
                      ""
                    )}
                    <Typography>
                      {valuePrefix}
                      {d.value}
                      {valueSuffix}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ValuesTableWrap>
      ) : (
        ""
      )}
    </Wrapper>
  )
}

export default Donut
